import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'
import { reaction, comparer } from 'mobx'
import DOMPurify from '@client/utils/dompurify'

import TextModel, { NULL as TextModelNULL } from './model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import { options } from './options'
import { updateDataInline } from './plugins/updateDataInline'
import {
  ITEM_TYPE_FORM,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_LANDING_PAGE,
  ITEM_TYPE_LANDING_PAGE_SUCCESS,
  ITEM_TYPE_SURVEY,
  ITEM_TYPE_SURVEY_THANK_YOU,
} from '@client/components/Editor/settings'

import './styles.scss'

@inject('app', 'editor', 'serverRender')
@observer
class TextEditor extends Component {
  static propTypes = {
    app: PropTypes.instanceOf(AppModel),
    model: PropTypes.instanceOf(TextModel),
    editor: PropTypes.instanceOf(EditorModel),
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    app: AppModelNULL,
    model: TextModelNULL,
    editor: EditorModelNULL,
    serverRender: false,
  }

  constructor(props) {
    super(props)

    // Store references to observable objects (not props) for use in reaction
    // This avoids the "Cannot read props in a reactive context" error
    const editor = props.editor
    const model = props.model

    // Track specific settings properties that updateDataInline uses
    // This ensures the reaction fires when any of these values change
    this.disposeReaction = reaction(
      () => {
        const values = editor.settings.values
        return {
          // Heading settings
          headings_h1FontSize: values.headings_h1FontSize,
          headings_h2FontSize: values.headings_h2FontSize,
          headings_h3FontSize: values.headings_h3FontSize,
          headings_h4FontSize: values.headings_h4FontSize,
          headings_color: values.headings_color,
          headings_fontWeight: values.headings_fontWeight,
          headings_fontFamily: values.headings_fontFamily,
          headings_lineHeight: values.headings_lineHeight,
          // Normal text settings
          normalText_color: values.normalText_color,
          normalText_fontFamily: values.normalText_fontFamily,
          normalText_fontSize: values.normalText_fontSize,
          normalText_lineHeight: values.normalText_lineHeight,
          // Link settings
          link_color: values.link_color,
        }
      },
      () => {
        const values = editor.settings.values
        model.setHtmlText(
          updateDataInline(values, model.htmlText)
        )
      },
      { delay: 100, equals: comparer.shallow }
    )
  }

  componentDidMount() {
    const { editor, model } = this.props
    model.setHtmlText(updateDataInline(editor.settings.values, model.htmlText))
  }

  componentWillUnmount() {
    if (this.disposeReaction) {
      this.disposeReaction()
    }
  }

  getStyles = () => {
    const {
      model: { settings },
    } = this.props
    const { backgroundActive, borderActive, ...restSettings } = settings.values
    const styles = {
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
      wordBreak: 'break-word',
    }
    if (backgroundActive) {
      styles.paddingTop = '10px'
      styles.paddingLeft = '10px'
      styles.paddingRight = '10px'
      styles.backgroundColor = restSettings.backgroundColor
      styles.borderRadius = `${restSettings.borderRadius}px`

      if (borderActive) {
        styles.borderColor = restSettings.borderColor
        styles.borderStyle = restSettings.borderStyle
        styles.borderWidth = `${restSettings.borderWidth}px`
      }
    }
    return styles
  }

  getPlaceholderStyles = () => {
    const globalSettings = this.props.editor?.settings?.values || {}
    const styles = {
      padding: '0px',
      color: 'rgb(136, 136, 136)',
      'font-size': '16px',
      'font-family': globalSettings.normalText_fontFamily,
      'font-weight': '400',
      'text-align': 'start',
      'list-style-position': 'outside',
      'line-height': globalSettings.normalText_lineHeight 
        ? `${globalSettings.normalText_lineHeight * 100}%` 
        : undefined,
      margin: '0 0 10px',
    }
    return Object
      .keys(styles)
      .filter((key) => styles[key] !== undefined)
      .map((key) => `${key}: ${styles[key]};`)
      .join('')
  }

  getPlaceholderContent = () => {
    const { app } = this.props
    const styles = this.getPlaceholderStyles()
    const content = `<p style="${styles}" data-inline="${styles}">${app.formatMessage(options.placeholder)}</p>`
    return DOMPurify.sanitize(content)
  }

  render() {
    const { model, serverRender, app, editor } = this.props
    const { htmlText, settings } = model
    const { backgroundActive } = settings.values
    const hasContent = htmlText && htmlText.trim() !== ''
    const content = hasContent
      ? htmlText
      : this.getPlaceholderContent()

    if (
      !backgroundActive ||
      [
        ITEM_TYPE_PAGE,
        ITEM_TYPE_FORM,
        ITEM_TYPE_LANDING_PAGE,
        ITEM_TYPE_LANDING_PAGE_SUCCESS,
        ITEM_TYPE_SURVEY,
        ITEM_TYPE_SURVEY_THANK_YOU,
      ].includes(editor.kind)
    ) {
      return (
        <div
          className={serverRender ? '' : 'text-block'}
          dangerouslySetInnerHTML={{ __html: content }}
          style={this.getStyles()}
        />
      )
    }
    return (
      <table
        cellSpacing="0"
        cellPadding="0"
        border="0"
        role="presentation"
        width="100%"
        className={serverRender ? '' : 'text-block'}
      >
        <tbody>
          <tr>
            <td style={this.getStyles()}>
              <div dangerouslySetInnerHTML={{ __html: content }} />
            </td>
          </tr>
        </tbody>
      </table>
    )
  }
}

export default TextEditor
